// The call one is in: one per app (the calls part makes it), kept outside the
// screens so a call goes on while the person moves around. Screens read it
// with `useVoice(session)` and act through its functions; only this holds the
// signalling socket and the connections.
//
// Flow: the click on "join" makes the audio context and starts the ICE
// servers and the microphone → the socket opens → for each person in hello
// and on peer.joined a connection is made and the voice added (both sides
// offer; perfect negotiation settles it).
//
// Promise:
// - The audio context is made in the click, before anything is awaited.
// - A join that the microphone's question outlived (another join or a leave
//   meanwhile, told by a generation number) is dropped and its microphone stopped.
// - Joining does not wait for noise suppression: the plain microphone goes
//   first and the processed track replaces it when ready. It starts once a
//   first connection connected (or 5 seconds on), not next to their making.
// - When the way breaks (any close but the final ones and a clean one), the
//   socket is opened again with `resume` after 0.5, 1, 2, 4, 4... seconds, the
//   connections and the elapsed time kept, for up to 25 seconds; then the call
//   ends. A resume that comes back as a new stay, or whose people are not the
//   same stays, joins again so every connection is made anew.
// - A connection that gathers no candidate is rebuilt by joining again (the
//   others make theirs again too), at most twice.
// - Mute and sharing are said again on every new socket. A microphone that
//   ends (unplugged) is asked for again and swapped in.
// - Leaving (and the page going away) sends `leave` before closing.
// - Cues: my joining once; others coming and going; none for those already
//   there nor on reconnecting.
import {
  VOICE_FINAL_CLOSES,
  VOICE_REJOIN_CLOSE,
  VOICE_RESUME_MS,
  type VoicePeerInfo,
  type VoiceServerMessage,
  type VoiceSignalData,
} from "@trameria/cloud-api/voice-shared";
import { useSyncExternalStore } from "react";
import { type CallAudio, createCallAudio } from "./audio";
import {
  type CreateDenoiser,
  type DenoiseLoader,
  type Denoiser,
  denoiseLoader,
  denoiseSetting,
} from "./denoise";
import { type LevelWatcher, levelWatcher } from "./level";
import { VoicePeer } from "./peer";
import { type ConnectSignal, connectSignal, type VoiceSignal } from "./signal";
import { cuePlayer, type PlayVoiceCue } from "./sounds";

export type VoiceStatus =
  | "idle"
  | "joining"
  | "joined"
  | "reconnecting"
  | "mic_denied"
  | "failed"
  | "closed";

export interface VoicePeerState {
  userId: string;
  muted: boolean;
  sharing: boolean;
  connection: RTCPeerConnectionState;
}

export interface VoiceState {
  status: VoiceStatus;
  slug: string | null;
  roomId: string | null;
  /** When the person joined (for the elapsed time; kept across reconnecting). */
  startedAt: number | null;
  muted: boolean;
  peers: readonly VoicePeerState[];
  remoteStreams: ReadonlyMap<string, MediaStream>;
  remoteScreens: ReadonlyMap<string, MediaStream>;
  /** My own shared screen (its preview), or null. */
  screen: MediaStream | null;
  /** Who is speaking now (me included). */
  speaking: ReadonlySet<string>;
  /** Whether noise suppression is wanted (this device). */
  denoise: boolean;
  denoiseStatus: "off" | "loading" | "on" | "unavailable";
  /** The code the socket was closed with when the call ended by itself. */
  closedCode: number | null;
}

export interface VoiceDeps {
  getUserMedia?(c: MediaStreamConstraints): Promise<MediaStream>;
  getDisplayMedia?(c: DisplayMediaStreamOptions): Promise<MediaStream>;
  connect?: ConnectSignal;
  createPeerConnection?(config: RTCConfiguration): RTCPeerConnection;
  audio?: CallAudio;
  playCue?: PlayVoiceCue;
  watchLevel?: LevelWatcher;
  denoise?: Pick<DenoiseLoader, "preload"> & { create: CreateDenoiser };
  /** Whether noise suppression is wanted, and keeping the answer (default: this device). */
  denoisePreference?: { read(): boolean; write(on: boolean): void };
  iceStallMs?: number;
  now?(): number;
}

/** How long noise suppression waits for a first connection before starting anyway. */
const DENOISE_WAIT_MS = 5_000;
/** How many times a connection that gathers nothing is rebuilt. */
const MAX_REBUILDS = 2;
/** Waits before opening the socket again after the way broke. */
const RECONNECT_DELAYS = [500, 1000, 2000, 4000];
/** The screen stops trying a little before the server lets the stay go. */
export const RECONNECT_FOR_MS = VOICE_RESUME_MS - 5_000;

const IDLE: VoiceState = {
  status: "idle",
  slug: null,
  roomId: null,
  startedAt: null,
  muted: false,
  peers: [],
  remoteStreams: new Map(),
  remoteScreens: new Map(),
  screen: null,
  speaking: new Set(),
  denoise: true,
  denoiseStatus: "off",
  closedCode: null,
};

interface Live {
  slug: string;
  roomId: string;
  self: string | null;
  /** This socket's id, to resume with. */
  connection: string | null;
  signal: VoiceSignal | null;
  /** The plain microphone (muting stops its track). */
  mic: MediaStream;
  /** What goes to the connections (processed when noise suppression is on). */
  stream: MediaStream;
  denoiser: Denoiser | null;
  denoisePending: boolean;
  /** Waiting for the first connection before starting noise suppression. */
  denoiseTimer: ReturnType<typeof setTimeout> | null;
  screen: MediaStream | null;
  iceServers: RTCIceServer[];
  peers: Map<string, { peer: VoicePeer; stay: string }>;
  levelStops: Map<string, () => void>;
  joined: boolean;
  rebuilds: number;
  drop: {
    since: number;
    attempt: number;
    code: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null;
  unwatch: () => void;
  onPageHide: () => void;
}

export interface VoiceSession {
  get(): VoiceState;
  subscribe(cb: () => void): () => void;
  /** Call in the click: it makes the audio context before awaiting anything. */
  join(input: {
    slug: string;
    roomId: string;
    iceServers: () => Promise<RTCIceServer[]>;
  }): Promise<void>;
  leave(): void;
  setMuted(muted: boolean): void;
  setDenoise(on: boolean): void;
  startScreenShare(): Promise<void>;
  stopScreenShare(): void;
}

export function useVoice(session: VoiceSession): VoiceState {
  return useSyncExternalStore(session.subscribe, session.get, session.get);
}

export function createVoiceSession(deps: VoiceDeps = {}): VoiceSession {
  const audio = deps.audio ?? createCallAudio();
  const playCue = deps.playCue ?? cuePlayer(audio);
  const watchLevel = deps.watchLevel ?? levelWatcher(audio);
  const denoise = deps.denoise ?? denoiseLoader(audio);
  const preference = deps.denoisePreference ?? {
    read: () => denoiseSetting.read(),
    write: (on: boolean) => denoiseSetting.write(on),
  };
  const connect = deps.connect ?? connectSignal;
  const now = deps.now ?? Date.now;
  const getUserMedia =
    deps.getUserMedia ??
    ((c: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(c));
  const getDisplayMedia =
    deps.getDisplayMedia ??
    ((c: DisplayMediaStreamOptions) =>
      navigator.mediaDevices.getDisplayMedia(c));

  let state: VoiceState = IDLE;
  const listeners = new Set<() => void>();
  let live: Live | null = null;
  let generation = 0;

  const setState = (patch: Partial<VoiceState>) => {
    state = { ...state, ...patch };
    for (const l of listeners) l();
  };

  const updatePeer = (userId: string, patch: Partial<VoicePeerState>) =>
    setState({
      peers: state.peers.map((p) =>
        p.userId === userId ? { ...p, ...patch } : p,
      ),
    });

  const setSpeaking = (userId: string, speaking: boolean) => {
    if (state.speaking.has(userId) === speaking) return;
    const next = new Set(state.speaking);
    if (speaking) next.add(userId);
    else next.delete(userId);
    setState({ speaking: next });
  };

  const stopLevel = (l: Live, userId: string) => {
    l.levelStops.get(userId)?.();
    l.levelStops.delete(userId);
    setSpeaking(userId, false);
  };

  const watchLevelOf = (l: Live, userId: string, stream: MediaStream) => {
    stopLevel(l, userId);
    l.levelStops.set(
      userId,
      watchLevel(stream, (speaking) => {
        if (live === l) setSpeaking(userId, speaking);
      }),
    );
  };

  const send = (l: Live, msg: Parameters<VoiceSignal["send"]>[0]) =>
    l.signal?.send(msg);

  function addPeer(l: Live, info: VoicePeerInfo) {
    if (!l.self) return;
    // The same person again (they replaced their stay): make it anew.
    if (l.peers.has(info.userId)) removePeer(l, info.userId);
    const userId = info.userId;
    const peer = new VoicePeer({
      polite: l.self < userId,
      iceServers: l.iceServers,
      localStream: l.stream,
      screenStream: l.screen,
      createPeerConnection: deps.createPeerConnection,
      send: (data: VoiceSignalData) =>
        send(l, { type: "signal", to: userId, data }),
      onRemoteStream: (stream) => {
        const next = new Map(state.remoteStreams);
        next.set(userId, stream);
        setState({ remoteStreams: next });
        watchLevelOf(l, userId, stream);
      },
      onRemoteScreen: (stream) => {
        const next = new Map(state.remoteScreens);
        next.set(userId, stream);
        setState({ remoteScreens: next });
      },
      onConnectionState: (connection) => {
        updatePeer(userId, { connection });
        if (connection === "connected") connected(l);
      },
      onIceStall: () => rebuild(l, userId),
      iceStallMs: deps.iceStallMs,
    });
    l.peers.set(userId, { peer, stay: info.stay });
    setState({
      peers: [
        ...state.peers.filter((p) => p.userId !== userId),
        {
          userId,
          muted: info.muted,
          sharing: info.sharing,
          connection: "new",
        },
      ],
    });
  }

  function removePeer(l: Live, userId: string) {
    l.peers.get(userId)?.peer.close();
    l.peers.delete(userId);
    stopLevel(l, userId);
    const streams = new Map(state.remoteStreams);
    streams.delete(userId);
    const screens = new Map(state.remoteScreens);
    screens.delete(userId);
    setState({
      peers: state.peers.filter((p) => p.userId !== userId),
      remoteStreams: streams,
      remoteScreens: screens,
    });
  }

  function removeAllPeers(l: Live) {
    for (const id of [...l.peers.keys()]) removePeer(l, id);
  }

  /** Whether the people I am connected to are the stays the server lists. */
  const sameStays = (l: Live, peers: readonly VoicePeerInfo[]) =>
    peers.length === l.peers.size &&
    peers.every((p) => l.peers.get(p.userId)?.stay === p.stay);

  function onHello(
    l: Live,
    msg: Extract<VoiceServerMessage, { type: "hello" }>,
  ) {
    const again = l.joined;
    l.self = msg.self;
    l.connection = msg.connection;
    if (l.drop?.timer) clearTimeout(l.drop.timer);
    l.drop = null;
    if (again && msg.resumed && !sameStays(l, msg.peers)) {
      // Someone came or went meanwhile: their signals were lost, so every
      // connection is made anew (joining again tells the others to).
      rejoin(l);
      return;
    }
    setState({
      status: "joined",
      startedAt: state.startedAt ?? now(),
      closedCode: null,
    });
    if (!again) {
      l.joined = true;
      playCue("joined");
      watchLevelOf(l, msg.self, l.stream);
    } else {
      // A new socket starts unmuted and not sharing on the server.
      if (state.muted) send(l, { type: "mute", muted: true });
      if (l.screen) send(l, { type: "screen", sharing: true });
    }
    if (msg.resumed) {
      for (const p of msg.peers)
        updatePeer(p.userId, { muted: p.muted, sharing: p.sharing });
      return;
    }
    removeAllPeers(l);
    for (const p of msg.peers) addPeer(l, p);
    if (!again) startDenoiserSoon(l);
  }

  /**
   * Noise suppression starts once the first connections are under way: with
   * nobody there at once; else when one connects, or after DENOISE_WAIT_MS.
   * Its worklet holds the audio thread for a moment, and Chromium sometimes
   * never gathers candidates for a connection made then (seen again on the
   * dev, 2026-10-18).
   */
  function startDenoiserSoon(l: Live) {
    if (!state.denoise || l.denoiser || l.denoisePending) return;
    if (l.peers.size === 0) {
      void attachDenoiser(l);
      return;
    }
    l.denoiseTimer = setTimeout(() => {
      l.denoiseTimer = null;
      if (live === l && !l.denoiser && !l.denoisePending && state.denoise)
        void attachDenoiser(l);
    }, DENOISE_WAIT_MS);
  }

  /** A connection connected: noise suppression may start now. */
  function connected(l: Live) {
    if (!l.denoiseTimer) return;
    clearTimeout(l.denoiseTimer);
    l.denoiseTimer = null;
    if (live === l && state.denoise) void attachDenoiser(l);
  }

  function handle(l: Live, msg: VoiceServerMessage) {
    if (live !== l) return;
    switch (msg.type) {
      case "hello":
        onHello(l, msg);
        return;
      case "peer.joined":
        addPeer(l, msg);
        playCue("peer_joined");
        return;
      case "peer.left":
        if (!l.peers.has(msg.userId)) return;
        removePeer(l, msg.userId);
        playCue("peer_left");
        return;
      case "peer.muted":
        updatePeer(msg.userId, { muted: msg.muted });
        return;
      case "peer.screen":
        updatePeer(msg.userId, { sharing: msg.sharing });
        if (!msg.sharing) {
          const screens = new Map(state.remoteScreens);
          screens.delete(msg.userId);
          setState({ remoteScreens: screens });
        }
        return;
      case "signal":
        void l.peers.get(msg.from)?.peer.handleSignal(msg.data);
        return;
    }
  }

  function open(l: Live, resume: string | null) {
    l.signal = connect({
      slug: l.slug,
      roomId: l.roomId,
      resume,
      onMessage: (msg) => handle(l, msg),
      onClose: (code) => {
        if (live !== l) return;
        l.signal = null;
        if (VOICE_FINAL_CLOSES.has(code) || code === 1000) end(l, code);
        else broke(l, code);
      },
    });
  }

  /** The way broke: open again with resume, a while later. */
  function broke(l: Live, code: number) {
    const t = now();
    l.drop ??= { since: t, attempt: 0, code, timer: null };
    if (t - l.drop.since >= RECONNECT_FOR_MS) {
      end(l, code);
      return;
    }
    setState({ status: "reconnecting" });
    const delay =
      RECONNECT_DELAYS[Math.min(l.drop.attempt, RECONNECT_DELAYS.length - 1)];
    l.drop.attempt += 1;
    l.drop.timer = setTimeout(() => {
      if (live === l && l.drop) open(l, l.connection);
    }, delay);
  }

  /** Joins again as a new stay (the others make their connections anew). */
  function rejoin(l: Live) {
    removeAllPeers(l);
    l.signal?.close(VOICE_REJOIN_CLOSE);
    open(l, null);
  }

  /** A connection gathers no candidate: make every connection again. */
  function rebuild(l: Live, userId: string) {
    if (live !== l) return;
    if (l.rebuilds >= MAX_REBUILDS) {
      console.warn(`voice: no candidates with ${userId} (rebuilt enough)`);
      return;
    }
    l.rebuilds += 1;
    console.warn(`voice: no candidates with ${userId}; rebuilding`);
    rejoin(l);
  }

  function teardown(l: Live) {
    for (const { peer } of l.peers.values()) peer.close();
    l.peers.clear();
    for (const stop of l.levelStops.values()) stop();
    l.levelStops.clear();
    l.denoiser?.close();
    for (const t of l.mic.getTracks()) {
      t.onended = null;
      t.stop();
    }
    for (const t of l.stream.getTracks()) t.stop();
    if (l.screen) for (const t of l.screen.getTracks()) t.stop();
    l.screen = null;
    if (l.drop?.timer) clearTimeout(l.drop.timer);
    l.drop = null;
    if (l.denoiseTimer) clearTimeout(l.denoiseTimer);
    l.denoiseTimer = null;
    l.unwatch();
    window.removeEventListener("pagehide", l.onPageHide);
  }

  function end(l: Live, code: number) {
    teardown(l);
    l.signal?.close();
    live = null;
    setState({
      status: "closed",
      closedCode: code,
      peers: [],
      remoteStreams: new Map(),
      remoteScreens: new Map(),
      screen: null,
      speaking: new Set(),
    });
  }

  async function attachDenoiser(l: Live) {
    l.denoisePending = true;
    let d: Denoiser | null = null;
    try {
      d = await denoise.create(l.mic);
    } catch (error) {
      console.warn("voice: noise suppression failed", error);
    }
    if (live !== l) {
      d?.close();
      return;
    }
    l.denoisePending = false;
    if (!d) {
      if (state.denoise) setState({ denoiseStatus: "unavailable" });
      return;
    }
    l.denoiser?.close();
    l.denoiser = d;
    l.stream = d.stream;
    d.setEnabled(state.denoise);
    const track = d.stream.getAudioTracks()[0];
    if (track)
      for (const { peer } of l.peers.values()) void peer.replaceTrack(track);
    if (l.self) watchLevelOf(l, l.self, d.stream);
    setState({ denoiseStatus: state.denoise ? "on" : "off" });
  }

  /** The microphone ended (unplugged, taken by the system): ask again and swap. */
  async function swapMic(l: Live) {
    let mic: MediaStream;
    try {
      mic = await getUserMedia({ audio: true });
    } catch (error) {
      console.warn("voice: the microphone could not be taken again", error);
      return;
    }
    if (live !== l) {
      for (const t of mic.getTracks()) t.stop();
      return;
    }
    for (const t of l.mic.getTracks()) {
      t.onended = null;
      t.stop();
    }
    l.mic = mic;
    for (const t of mic.getAudioTracks()) t.enabled = !state.muted;
    watchMicEnd(l);
    l.denoiser?.close();
    l.denoiser = null;
    l.stream = mic;
    const track = mic.getAudioTracks()[0];
    if (track)
      for (const { peer } of l.peers.values()) void peer.replaceTrack(track);
    if (l.self) watchLevelOf(l, l.self, mic);
    if (state.denoise) void attachDenoiser(l);
  }

  function watchMicEnd(l: Live) {
    for (const t of l.mic.getAudioTracks())
      t.onended = () => {
        if (live === l) void swapMic(l);
      };
  }

  function leave() {
    generation += 1;
    const l = live;
    if (l) {
      send(l, { type: "leave" });
      teardown(l);
      l.signal?.close();
      live = null;
    }
    state = IDLE;
    for (const cb of listeners) cb();
  }

  return {
    get: () => state,
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },

    async join(input) {
      if (live) leave();
      generation += 1;
      const gen = generation;
      const wantDenoise = preference.read();
      setState({
        ...IDLE,
        status: "joining",
        slug: input.slug,
        roomId: input.roomId,
        denoise: wantDenoise,
        denoiseStatus: wantDenoise ? "loading" : "off",
      });
      // In the click, before anything is awaited (Safari keeps a context made
      // later suspended).
      audio.ensure();
      if (wantDenoise) denoise.preload();
      const ice = input.iceServers();
      ice.catch(() => {});
      let mic: MediaStream;
      try {
        mic = await getUserMedia({ audio: true });
      } catch {
        if (gen === generation) setState({ status: "mic_denied" });
        return;
      }
      if (gen !== generation) {
        for (const t of mic.getTracks()) t.stop();
        return;
      }
      let iceServers: RTCIceServer[];
      try {
        iceServers = await ice;
      } catch {
        for (const t of mic.getTracks()) t.stop();
        if (gen === generation) setState({ status: "failed" });
        return;
      }
      if (gen !== generation) {
        for (const t of mic.getTracks()) t.stop();
        return;
      }
      const l: Live = {
        slug: input.slug,
        roomId: input.roomId,
        self: null,
        connection: null,
        signal: null,
        mic,
        stream: mic,
        denoiser: null,
        denoisePending: false,
        denoiseTimer: null,
        screen: null,
        iceServers,
        peers: new Map(),
        levelStops: new Map(),
        joined: false,
        rebuilds: 0,
        drop: null,
        unwatch: audio.watch(),
        onPageHide: () => {
          if (live !== l) return;
          send(l, { type: "leave" });
          l.signal?.close();
        },
      };
      live = l;
      window.addEventListener("pagehide", l.onPageHide);
      watchMicEnd(l);
      open(l, null);
    },

    leave,

    setMuted(muted) {
      const l = live;
      if (!l) return;
      // The plain microphone stops: silence flows through noise suppression too.
      for (const t of l.mic.getAudioTracks()) t.enabled = !muted;
      send(l, { type: "mute", muted });
      setState({ muted });
    },

    setDenoise(on) {
      preference.write(on);
      const l = live;
      if (!l) {
        setState({ denoise: on });
        return;
      }
      l.denoiser?.setEnabled(on);
      if (on && !l.denoiser && !l.denoisePending) void attachDenoiser(l);
      // Without a denoiser it is being made now (it says unavailable if it fails).
      setState({
        denoise: on,
        denoiseStatus: !on ? "off" : l.denoiser ? "on" : "loading",
      });
    },

    async startScreenShare() {
      const l = live;
      if (!l || l.screen) return;
      let stream: MediaStream;
      try {
        stream = await getDisplayMedia({
          // Text should be readable: the size is left to the device, only the rate is kept low.
          video: { frameRate: { ideal: 15, max: 15 } },
          audio: false,
        });
      } catch {
        return; // closed without choosing
      }
      if (live !== l || l.screen) {
        for (const t of stream.getTracks()) t.stop();
        return;
      }
      const track = stream.getVideoTracks()[0];
      if (!track) return;
      // Mostly text: sharpness before motion.
      if ("contentHint" in track) track.contentHint = "detail";
      track.onended = () => {
        if (live === l && l.screen === stream) stopScreenShare();
      };
      l.screen = stream;
      for (const { peer } of l.peers.values()) peer.setScreen(stream);
      send(l, { type: "screen", sharing: true });
      setState({ screen: stream });
    },

    stopScreenShare,
  };

  function stopScreenShare() {
    const l = live;
    if (!l?.screen) return;
    const stream = l.screen;
    l.screen = null;
    for (const { peer } of l.peers.values()) peer.setScreen(null);
    for (const t of stream.getTracks()) {
      t.onended = null;
      t.stop();
    }
    send(l, { type: "screen", sharing: false });
    setState({ screen: null });
  }
}
